/**
 * SUB-4.7 · FR74, AC2. One place that knows every cache keyed by USER rather
 * than by SCOPE, so that a scope change can empty all of them.
 *
 * > Given tenant and permission caches keyed by user rather than scope, when
 * > scope changes, then they are invalidated, so a merged view never serves
 * > single-account data or the reverse.
 *
 * ⚠️ WHY A REGISTRY AND NOT FOUR IMPORTS.
 *
 * `TenantContext` is where a scope change is detected, and it already clears the
 * caches it owns. The permission cache lives inside `PermissionContext`, which
 * imports `useTenant` from `TenantContext`; importing its clear function back
 * the other way makes those two modules a cycle. A cycle here is not a style
 * complaint: the module that loses the race initialises with an undefined
 * binding, and the symptom is a cache that silently stops being cleared, which
 * is exactly the failure this file exists to prevent and is invisible until
 * someone is looking at another account's permissions.
 *
 * So the direction of knowledge is inverted. Nothing here imports a context.
 * Each cache owner registers its own clear at module load, and the detector
 * calls one function without knowing who is listening.
 *
 * ⚠️ WHAT BELONGS HERE, AND WHAT DOES NOT.
 *
 * Register a cache when its key does NOT distinguish merged scope from
 * single-account scope on the same anchor. In merged scope
 * `get_current_user_tenant_id()` returns the ANCHOR, which for a tenant user is
 * their own parent account, so `…-${currentTenant.tenant_id}` is the SAME STRING
 * in both scopes and a per-tenant key is not a per-scope key. That collision is
 * the entire finding; a key carrying a tenant id reads as though it were safe.
 *
 * Do not register anything whose key already carries `scopeReachKey`. Clearing
 * it would be a needless refetch, and a registry that clears everything teaches
 * callers to stop registering.
 *
 * ⚠️ REGISTRATION REQUIRES THE MODULE TO HAVE BEEN LOADED, AND THAT IS THE ONE
 * WAY THIS PATTERN FAILS QUIETLY. A registration in a module nothing on the
 * current route imports never runs, and a production bundler may drop an import
 * kept only for its side effect. So register ONLY from modules that are always
 * loaded — `PermissionContext` is a provider mounted in `App.tsx`, and it has to
 * be registered rather than imported because the reverse import would be a
 * cycle. Everything `TenantContext` already imports for other reasons, such as
 * `pipelinePaginationService`, is called directly from `dropScopedCaches`
 * instead: there is no cycle to avoid, and a direct call cannot be
 * tree-shaken out of existence.
 */

type CacheClear = () => void;

const registry = new Map<string, CacheClear>();

/**
 * Register a cache to be emptied whenever the session's reach changes.
 *
 * Keyed by name and idempotent, so a module re-evaluated by Vite's HMR replaces
 * its entry instead of accumulating one per reload.
 *
 * @param name  Stable identifier, used only for diagnostics and de-duplication.
 * @param clear Empties the cache. Must be safe to call when it is already empty
 *              and must not throw: it runs inside the scope-change path, and a
 *              throw there would leave the caches AFTER it in the map populated.
 */
export function registerScopeCache(name: string, clear: CacheClear): void {
  registry.set(name, clear);
}

/** Test seam. Not called by application code. */
export function unregisterScopeCache(name: string): void {
  registry.delete(name);
}

/** The names currently registered, for the assertion in `scopeCaches.test.ts`. */
export function registeredScopeCaches(): string[] {
  return [...registry.keys()].sort();
}

/**
 * Empty every registered cache. Returns the names cleared, so the caller can log
 * what actually happened rather than what it intended.
 *
 * ⚠️ ONE FAILURE MUST NOT SPARE THE REST. Each clear is called inside its own
 * try, because the whole reason for a central sweep is that a half-swept tree
 * serves one scope's answers next to another's.
 */
export function clearScopeCaches(): string[] {
  const cleared: string[] = [];
  registry.forEach((clear, name) => {
    try {
      clear();
      cleared.push(name);
    } catch (error) {
      console.error(`[scopeCaches] "${name}" failed to clear:`, error);
    }
  });
  return cleared.sort();
}
